// web/src/components/records/ProofIntervalControl.tsx — owner-only cadence control
// (M2.7). The record owner picks how often holders are asked to prove they still
// hold their shard (daily / weekly / monthly) via api.setProofInterval. Pure policy
// metadata — no shard, key, or plaintext is touched (R11). Used on RecordDetail and
// surfaced read-only in the seal wizard's quorum step.
import React, { useState } from 'react';
import { Timer } from 'lucide-react';
import { api } from '../../api';
import { Icon } from '../ui/Icon';
import { FeedbackMessage } from '../ui';

const DAY = 86_400;
export const PROOF_CADENCES: ReadonlyArray<{ label: string; seconds: number }> = [
  { label: 'Daily', seconds: DAY },
  { label: 'Weekly', seconds: 7 * DAY },
  { label: 'Monthly', seconds: 30 * DAY },
] as const;

/** Snap an arbitrary interval to the nearest preset for the select's value. */
function nearestCadence(seconds: number): number {
  let best = PROOF_CADENCES[0].seconds;
  let bestDelta = Infinity;
  for (const c of PROOF_CADENCES) {
    const d = Math.abs(c.seconds - seconds);
    if (d < bestDelta) {
      bestDelta = d;
      best = c.seconds;
    }
  }
  return best;
}

export function ProofIntervalControl({
  recordId,
  proofIntervalSec,
  onChanged,
}: {
  recordId: string;
  proofIntervalSec: number;
  onChanged?: (proofIntervalSec: number) => void;
}): React.JSX.Element {
  const [value, setValue] = useState<number>(() => nearestCadence(proofIntervalSec));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function apply(next: number): Promise<void> {
    setValue(next);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await api.setProofInterval(recordId, next);
      setSaved(true);
      onChanged?.(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bgKvList">
      <div className="bgKv">
        <span className="bgKvKey">
          <Icon icon={Timer} size="xs" aria-hidden="true" /> Proof cadence
        </span>
        <span className="bgKvVal">
          <select
            className="input"
            aria-label="How often holders are asked to prove they hold their shard"
            value={value}
            disabled={busy}
            onChange={(e) => void apply(Number(e.target.value))}
          >
            {PROOF_CADENCES.map((c) => (
              <option key={c.seconds} value={c.seconds}>
                {c.label}
              </option>
            ))}
          </select>
        </span>
      </div>
      <p className="bgSubtle" style={{ margin: 0 }}>
        How often each holder is asked to prove they still hold their shard. Proving
        reveals nothing — it only confirms key control.
      </p>
      {saved && !error && <FeedbackMessage ok msg="Proof cadence updated." />}
      {error && <FeedbackMessage ok={false} msg={error} />}
    </div>
  );
}

export default ProofIntervalControl;
